import {
  Order,
  OrderAcquisitionType,
  OrderPrice,
  Shipment,
  ShipmentStopType,
  Stop,
  StopCompletionReason,
  TimeWindow,
} from "@api/graphql/generated/generated-types";
import {
  OrderDetailsWidgetDataProps,
  GeneralPanelProps,
  StopPanelProps,
  StopPanelPropsBodyItem,
  PricingPanelProps,
} from "@src/common/components/Widgets/OrderDetailsWidget";
import { displayShipmentStatus } from "@src/shipments/utils/shipmentStatusDisplayer";
import { formatStopAddress } from "./stopFullAddressFormatter";
import { formatTime, formatDate } from "@src/common/lib/DateUtils";
import moment from "moment-timezone";
import { momentToTimePickerDate } from "@src/common/utils/timezoneConverters";
import { formatPhoneNumber } from "@src/common/lib/textUtils";
import { InternalNotePanelProps } from "@src/common/components/Widgets/InternalNote";
import { getCompletedAtText } from "./shipmentRowFormatter";
import { formatCurrency } from "@src/common/courierLocale";
import { getJobDuration, getEstimatedPay } from "./jobDuration";
import { shouldSortByEntryOrder } from "@src/shipments/features/stopReordering";

export function makeOrderDetailsWidgetPropsFromShipment(
  shipment: Shipment | undefined,
  orderPrice?: OrderPrice | undefined,
  currencyDisplay: string = "$",
  shouldKeepOrderEntrySequence: boolean = false
): OrderDetailsWidgetDataProps[] {
  if (!shipment) {
    return [];
  }

  const generalPanelProps: GeneralPanelProps[] = makeGeneralPanelProps(
    shipment,
    currencyDisplay
  );
  const stopPanelProps: StopPanelProps[] = makeStopPanelProps(
    shipment,
    shouldKeepOrderEntrySequence
  );
  const internalNotePanelProps: InternalNotePanelProps =
    makeInternalNotePanelProps(shipment);

  const panels: OrderDetailsWidgetDataProps[] = [
    {
      panelName: "internalNote",
      internalNotePanelProps: internalNotePanelProps,
    },
    // Above the stops: order labels drive dispatch views through automations,
    // so a dispatcher verifying a bulk relabel should reach them without
    // scrolling past the stop list. Always present, so "no labels" is legible
    // too.
    {
      panelName: "Labels",
      orderLabels: shipment.order.assignedLabels || [],
    },
    {
      panelName: "Stops",
      stopPanelProps: stopPanelProps,
      generalPanelProps: [],
      hasPricingRoute: (shipment.order.pricingStopIds?.length || 0) > 0,
    },
  ];

  if (shipment.packageDescription) {
    panels.push({
      panelName: "Package Description",
      packageDescription: shipment.packageDescription,
    });
  }
  if (shipment.packageItems) {
    panels.push({
      panelName: "Package Items",
      packageItems: shipment.packageItems,
    });
  }

  if (orderPrice) {
    const pricingPanelProps: PricingPanelProps = {
      title: Number(orderPrice.totalAfterTax),
      value: "Order Total",
      breakdown: orderPrice.pricingBreakDown,
      pricingList: undefined,
      showAnimation: false,
      taxBreakDown: orderPrice.taxBreakDown,
      total: orderPrice.total ? Number(orderPrice.total) : undefined,
      totalAfterTax: orderPrice.totalAfterTax
        ? Number(orderPrice.totalAfterTax)
        : undefined,
    };

    panels.push({
      panelName: "Pricing",
      pricingPanelProps: pricingPanelProps,
      generalPanelProps: [],
      stopPanelProps: [],
    });
  }

  panels.push({
    panelName: "General",
    generalPanelProps: generalPanelProps,
    stopPanelProps: [],
  });

  if (shipment.order.additionalInfoCount > 0) {
    panels.push({
      panelName: "Additional Information",
      additionalInfoPanelProps: {
        count: shipment.order.additionalInfoCount,
        orderId: shipment.order.id,
      },
    });
  }

  return panels;
}

function makeGeneralPanelProps(
  shipment: Shipment,
  currencyDisplay: string = "$"
): GeneralPanelProps[] {
  let result = [
    {
      title: "ID",
      value: String(shipment.order.displayId),
    },
    {
      title: "Tracking",
      value: shipment.trackingNumber || "",
    },
    {
      title: "Customer",
      value: shipment.order.endCustomer.companyName || "",
    },
    {
      title: "Customer ID",
      value: String(shipment.order.endCustomer.displayId ?? ""),
    },
    {
      title: "Customer Reference #",
      value: shipment.order.endCustomerReferenceNumber || "",
    },
    {
      title: "Caller",
      value: shipment.order.caller || "",
    },
    {
      title: "Status",
      value: displayShipmentStatus(shipment),
    },
    {
      title: "Created By",
      value: shipment.order.acquisitionType
        ? getAcquisitionTypeLabel(
            shipment.order.acquisitionType,
            shipment.order
          )
        : "",
    },
    {
      title: "Created At",
      value: formatDateTimeInCourierTZ(shipment.createdAt),
    },
    {
      title: "Dispatched By",
      value: shipment.dispatchedByUser ? shipment.dispatchedByUser.email : "",
    },
    {
      title: "Dispatched At",
      value: formatDateTimeInCourierTZ(shipment.assignedAt),
    },
    {
      title: "Driver Confirmed At",
      value: formatDateTimeInCourierTZ(shipment.driverConfirmedAt),
    },
  ];
  const callerPhone = shipment.order.callerPhone;
  if (callerPhone) {
    result.push({
      title: "Caller Phone",
      value: formatPhoneNumber(callerPhone),
    });
  }
  if (shipment.completedDate) {
    result.push({
      title: "Completed On",
      value:
        formatDate(shipment.completedDate) +
        (shipment.hasCompletedDateOverride ? " (Overridden)" : ""),
    });
    if (shipment.hasCompletedDateOverride) {
      result.push({
        title: "Completed Date Overridden By",
        value: shipment.completedDateOverriddenBy || "",
      });
    }
  }
  // Set by order automation (destination-based), not a user — shown only when present, since
  // there is no always-displayed base tax code row to suffix here.
  if (shipment.order.taxCodeOverride) {
    result.push({
      title: "Tax Code (Overridden)",
      value: shipment.order.taxCodeOverride,
    });
  }
  // Pickup arrival -> dropoff completion elapsed time (for hourly driver pay).
  // Row is omitted entirely for in-progress/incomplete jobs.
  const jobDuration = getJobDuration(shipment);
  if (jobDuration) {
    result.push({
      title: "Job Duration",
      value: jobDuration.label,
    });
    const estimatedPay = getEstimatedPay(shipment, jobDuration.decimalHours);
    if (estimatedPay !== null) {
      result.push({
        title: "Est. Driver Pay",
        value: formatCurrency(estimatedPay, currencyDisplay),
      });
    }
  }
  const customFieldValues = shipment.order.customFieldValues;
  const endCustomerCustomFields = shipment.order.endCustomer.customFields;
  if (customFieldValues) {
    const customFieldProps = customFieldValues
      .filter((customField) => {
        if (customField.valueText) {
          return true;
        }
        return endCustomerCustomFields?.some(
          (field) => field.id === customField.fieldId
        );
      })
      .map((customField) => {
        return {
          title: customField.field.name,
          value: customField.valueDisplay || "",
        };
      });
    result = [...result, ...customFieldProps];
  }
  return result;
}

function getAcquisitionTypeLabel(
  acquisitionType: OrderAcquisitionType,
  order: Order
): string {
  switch (acquisitionType) {
    case OrderAcquisitionType.CustomerPortal:
      if (order.createdByEndUser) {
        return order.createdByEndUser.email;
      }
      if (order.createdByEndCustomer?.companyName) {
        return order.createdByEndCustomer.companyName;
      }
      return "Customer Portal";
    case OrderAcquisitionType.User:
      if (order.createdByUser?.email) {
        return order.createdByUser.email;
      }
      return "Dispatcher";
    default:
      return "N/A";
  }
}

function makeStopPanelProps(
  shipment: Shipment,
  shouldKeepOrderEntrySequence: boolean
): StopPanelProps[] {
  // "Keep the Order Entry Sequence" couriers: the entry order is what the
  // customer typed, so neither the pricing route nor driver re-sequencing
  // (Prioritize Stop, route splits) may reorder it after creation.
  if (shipment.order.pricingStopIds && !shouldKeepOrderEntrySequence) {
    const pricingStopIdsToSortOrder = shipment.order.pricingStopIds.reduce(
      (acc, value, index) => {
        acc[value] = index;
        return acc;
      },
      {} as Record<string, number>
    );
    return [
      ...shipment.stops
        .sort((a, b) => {
          const aIndex = pricingStopIdsToSortOrder[a.id];
          const bIndex = pricingStopIdsToSortOrder[b.id];
          return aIndex - bIndex;
        })
        .map((stop) => makeStopPanelPropsFromStop(stop)),
    ];
  }

  const hasAnyTaskSequence = shipment.stops.some(
    (stop) => stop.tasks?.[0]?.sequenceNumber != null
  );

  const hasAnyEntrySequence: boolean = shipment.stops.some(
    (stop) => stop.stopConsolidatedSortingNumber != null
  );

  const shouldSortByTaskSequence: boolean =
    hasAnyTaskSequence &&
    !(shouldKeepOrderEntrySequence && hasAnyEntrySequence);

  const shouldSortByEntryNumber: boolean = shouldSortByEntryOrder({
    stops: shipment.stops,
    shouldKeepOrderEntrySequence,
  });

  const sortKey = (stop: Stop): number => {
    if (shouldSortByTaskSequence) {
      return stop.tasks?.[0]?.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
    }
    if (shouldSortByEntryNumber) {
      return stop.orderEntrySequenceNumber ?? Number.MAX_SAFE_INTEGER;
    }
    return stop.stopConsolidatedSortingNumber ?? Number.MAX_SAFE_INTEGER;
  };

  const filterByTypeAndSort = (type: ShipmentStopType) =>
    shipment.stops
      .filter((stop) => stop.type === type)
      .sort((a, b) => sortKey(a) - sortKey(b))
      .map((stop) => makeStopPanelPropsFromStop(stop));

  return [
    ...filterByTypeAndSort(ShipmentStopType.PickUp),
    ...filterByTypeAndSort(ShipmentStopType.DropOff),
    ...filterByTypeAndSort(ShipmentStopType.Return),
  ];
}

function makeInternalNotePanelProps(shipment: Shipment): any {
  return {
    shipmentId: shipment.id,
    textareaValue: shipment.internalNote || "",
  };
}

export function makeStopPanelPropsFromStop(stop: Stop): StopPanelProps {
  const bodyItems: StopPanelPropsBodyItem[] = [];
  if (stop.stopDate) {
    bodyItems.push({
      title: "Route Date",
      value: stop.stopDate,
    });
  }
  if (stop.companyName) {
    bodyItems.push({
      title: "Company",
      value: stop.companyName,
    });
  }
  if (stop.contactName) {
    bodyItems.push({
      title: "Contact Name",
      value: stop.contactName,
    });
  }
  if (stop.phone) {
    bodyItems.push({
      title: "Phone",
      value: formatPhoneNumber(stop.phone),
    });
  }
  bodyItems.push({
    title: "Address",
    value: formatStopAddress(stop),
  });
  if (stop.suite) {
    bodyItems.push({
      title: "Suite",
      value: stop.suite,
    });
  }
  if (stop.instruction) {
    bodyItems.push({
      title: "Instruction",
      value: stop.instruction,
      className: "text-blue-500",
    });
  }
  if (stop.driverNote) {
    bodyItems.push({
      title: "Driver Note",
      value: stop.driverNote,
      className: "text-yellow-300",
    });
  }

  const eta: string | undefined = formatEta(stop);
  const arrivedAt: string | undefined = formatArrivedAt(stop);
  const completedAt = getCompletedAtText(stop);
  const isAttempted = stop.completionReason === StopCompletionReason.Attempted;

  if (eta && !arrivedAt) {
    bodyItems.push({
      title: "ETA",
      value: eta,
    });
  }
  if (arrivedAt) {
    bodyItems.push({
      title: "Arrived",
      value: arrivedAt,
    });
  }
  if (completedAt && !isAttempted) {
    bodyItems.push({
      title: "Completed",
      value: completedAt,
      className: "text-green-500",
    });
  } else if (completedAt && isAttempted) {
    bodyItems.push({
      title: "Attempted",
      value: completedAt,
      className: "text-orange-400",
    });
  }

  return {
    title: stop.type,
    subtext: formatTimeWindow(stop.timeWindow),
    bodyItems: bodyItems,
    stop: stop,
  };
}

function formatEta(stop: Stop): string | undefined {
  if (!stop.tasks || stop.tasks.length === 0) {
    return undefined;
  }

  const taskWithEta = stop.tasks.find(
    (task) =>
      task.etaEpochSeconds !== null && task.etaEpochSeconds !== undefined
  );
  if (!taskWithEta) {
    return undefined;
  }

  // Use moment to format in courier's timezone (via global setDefault)
  return moment(taskWithEta.etaEpochSeconds * 1000).format("LT");
}

function formatArrivedAt(stop: Stop): string | undefined {
  if (!stop.tasks || stop.tasks.length === 0) {
    return undefined;
  }
  if (!stop.tasks[0].arrivedAt) {
    return undefined;
  }
  const taskWithArrivedAt = stop.tasks.find(
    (task) => task.arrivedAt !== null && task.arrivedAt !== undefined
  );

  if (!taskWithArrivedAt) {
    return undefined;
  }

  // Use moment to format in courier's timezone (via global setDefault)
  return moment(parseInt(taskWithArrivedAt.arrivedAt) * 1000).format("LT");
}

// Format timestamp in courier's timezone (via global moment.tz.setDefault)
function formatDateTimeInCourierTZ(timestamp: number | undefined): string {
  if (!timestamp) return "";
  return moment(timestamp * 1000).format("h:mm A, MMM D");
}

function formatTimeWindow(timeWindow: TimeWindow): string {
  // Convert Unix timestamp (courier TZ) to Date that displays courier's visual time
  // moment.unix() interprets in courier TZ (via global setDefault)
  // momentToTimePickerDate() creates a browser Date showing the same visual time
  const toDisplayDate = (ts: number | undefined): Date | undefined =>
    ts ? momentToTimePickerDate(moment.unix(ts)) : undefined;

  const windowOpen = toDisplayDate(timeWindow.open);
  const windowClose = toDisplayDate(timeWindow.close);

  if (!windowOpen && !windowClose) {
    return "No time window specified";
  }

  if (!windowOpen) {
    return `Deliver by ${formatTime(windowClose, "LT, MMM Do")}`;
  }
  if (!windowClose) {
    return `Ready ${formatTime(windowOpen)}`;
  }

  return `Between ${formatTime(windowOpen)} - ${formatTime(windowClose)}`;
}
